import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, kpiAPI, monitoringAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  DollarSign, 
  Brain, 
  Shield, 
  Activity,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  GraduationCap,
  Heart,
  Sparkles,
  ArrowUpRight,
  BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, variant = 'default', description }) => {
  const variants = {
    default: 'bg-white border border-slate-200',
    primary: 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-none',
    success: 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-none',
    warning: 'bg-gradient-to-br from-amber-500 to-amber-600 text-white border-none',
    ai: 'bg-gradient-to-br from-violet-500 to-purple-600 text-white border-none'
  };

  const isLight = variant !== 'default';

  return (
    <Card className={`${variants[variant]} card-hover`} data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className={`text-sm font-medium ${isLight ? 'text-white/80' : 'text-slate-500'}`}>
              {title}
            </p>
            <p className={`text-3xl font-bold tracking-tight ${isLight ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
              {value}
            </p>
            {description && (
              <p className={`text-sm ${isLight ? 'text-white/70' : 'text-slate-500'}`}>
                {description}
              </p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isLight ? 'bg-white/20' : 'bg-slate-100'
          }`}>
            <Icon className={`w-6 h-6 ${isLight ? 'text-white' : 'text-indigo-600'}`} />
          </div>
        </div>
        {trend && (
          <div className="flex items-center gap-1 mt-4">
            {trend === 'up' ? (
              <TrendingUp className={`w-4 h-4 ${isLight ? 'text-white' : 'text-emerald-500'}`} />
            ) : (
              <TrendingDown className={`w-4 h-4 ${isLight ? 'text-white' : 'text-red-500'}`} />
            )}
            <span className={`text-sm font-medium ${isLight ? 'text-white' : trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
              {trendValue}
            </span>
            <span className={`text-sm ${isLight ? 'text-white/70' : 'text-slate-500'}`}>
              vs last week
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const KPIGauge = ({ score, label }) => {
  const getColor = (score) => {
    if (score >= 85) return '#10B981';
    if (score >= 70) return '#3B82F6';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-28 h-28">
        <svg className="w-full h-full transform -rotate-90">
          <circle
            cx="56"
            cy="56"
            r="45"
            fill="none"
            stroke="#E2E8F0"
            strokeWidth="10"
          />
          <circle
            cx="56"
            cy="56"
            r="45"
            fill="none"
            stroke={getColor(score)}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {score}%
          </span>
        </div>
      </div>
      <p className="mt-2 text-sm font-medium text-slate-600">{label}</p>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [kpiData, setKpiData] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [user?.employee_id]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [dashRes, kpiRes] = await Promise.all([
        dashboardAPI.getEmployeeDashboard(user?.employee_id || '100001'),
        kpiAPI.getByEmployee(user?.employee_id || '100001')
      ]);

      setDashboardData(dashRes.data);
      setKpiData(kpiRes.data);
      // Set default health data since health API is not implemented yet
      setHealthData({
        stress_level: 'low',
        burnout_risk: 'low',
        work_balance: 'Good',
        engagement_score: 82
      });
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const currentKpi = (Array.isArray(kpiData) ? kpiData[0] : kpiData) || dashboardData?.current_kpi || {
    productivity_score: 84,
    focus_score: 78,
    task_completion: 92,
    training_score: 75,
    attendance_score: 98,
    wellness_score: 82,
    overall_score: 84.7,
    quality_score: 85,
    collaboration_score: 80,
    innovation_score: 75
  };

  // Generate chart data - handle both array and single object KPI data
  const generateChartData = () => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    if (Array.isArray(kpiData) && kpiData.length > 0) {
      return kpiData.slice(0, 7).reverse().map((kpi, index) => ({
        day: days[index] || `Day ${index + 1}`,
        productivity: kpi.productivity_score || 80,
        focus: kpi.focus_score || kpi.quality_score || 75,
        wellness: kpi.wellness_score || kpi.collaboration_score || 78
      }));
    }
    // Generate from single KPI object
    return days.map((day, index) => ({
      day,
      productivity: Math.round((currentKpi.productivity_score || 80) + (Math.random() * 10 - 5)),
      focus: Math.round((currentKpi.quality_score || currentKpi.focus_score || 75) + (Math.random() * 10 - 5)),
      wellness: Math.round((currentKpi.collaboration_score || currentKpi.wellness_score || 78) + (Math.random() * 10 - 5))
    }));
  };

  const chartData = generateChartData();

  const getStressColor = (level) => {
    switch (level) {
      case 'low': return 'bg-emerald-100 text-emerald-700';
      case 'medium': return 'bg-amber-100 text-amber-700';
      case 'high': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 border-4 border-white shadow-lg">
            <AvatarImage src={user?.avatar} alt={user?.first_name} />
            <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xl font-bold">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          <div>
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome back, {user?.first_name}!
            </h2>
            <p className="text-slate-500">
              {user?.position} • {user?.department}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle className="w-3 h-3 mr-1" />
            Active
          </Badge>
          <Badge variant="outline" className="px-3 py-1.5 bg-violet-50 text-violet-700 border-violet-200">
            <Sparkles className="w-3 h-3 mr-1" />
            AI Monitoring
          </Badge>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Overall KPI"
          value={`${Math.round(currentKpi.overall_score)}%`}
          icon={BarChart3}
          trend="up"
          trendValue="+5.2%"
          variant="primary"
        />
        <StatCard
          title="Productivity"
          value={`${currentKpi.productivity_score}%`}
          icon={Activity}
          trend="up"
          trendValue="+3.8%"
        />
        <StatCard
          title="Wellness Score"
          value={`${currentKpi.collaboration_score || currentKpi.wellness_score || 82}%`}
          icon={Heart}
          trend="up"
          trendValue="+2.1%"
          variant="success"
        />
        <StatCard
          title="AI Analysis"
          value="Active"
          icon={Brain}
          description="Real-time monitoring"
          variant="ai"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* KPI Chart */}
        <Card className="lg:col-span-2" data-testid="kpi-chart-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Performance Trends
                </CardTitle>
                <CardDescription>Your KPI metrics over the past week</CardDescription>
              </div>
              <Button variant="outline" size="sm">
                View Details
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="productivityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="focusGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="day" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} domain={[60, 100]} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="productivity" 
                    stroke="#4F46E5" 
                    strokeWidth={2}
                    fill="url(#productivityGradient)" 
                    name="Productivity"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="focus" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    fill="url(#focusGradient)" 
                    name="Focus"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* KPI Breakdown */}
        <Card data-testid="kpi-breakdown-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              KPI Breakdown
            </CardTitle>
            <CardDescription>Individual score components</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {[
              { label: 'Productivity', value: currentKpi.productivity_score || 84, color: 'bg-indigo-500' },
              { label: 'Quality', value: currentKpi.quality_score || currentKpi.task_completion || 85, color: 'bg-emerald-500' },
              { label: 'Attendance', value: currentKpi.attendance_score || 98, color: 'bg-blue-500' },
              { label: 'Collaboration', value: currentKpi.collaboration_score || currentKpi.training_score || 80, color: 'bg-violet-500' },
              { label: 'Innovation', value: currentKpi.innovation_score || currentKpi.focus_score || 75, color: 'bg-amber-500' },
              { label: 'Overall', value: currentKpi.overall_score || 84, color: 'bg-pink-500' },
            ].map((item) => (
              <div key={item.label} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{item.label}</span>
                  <span className="font-semibold text-slate-900">{item.value}%</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${item.color} rounded-full transition-all duration-500`}
                    style={{ width: `${item.value}%` }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Health Status */}
        <Card data-testid="health-status-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Heart className="w-5 h-5 text-pink-500" />
              Health Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Stress Level</span>
              <Badge className={getStressColor(healthData?.stress_level || 'low')}>
                {healthData?.stress_level || 'Low'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Burnout Risk</span>
              <Badge className={getStressColor(healthData?.burnout_risk || 'low')}>
                {healthData?.burnout_risk || 'Low'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Work-Life Balance</span>
              <Badge className="bg-emerald-100 text-emerald-700">
                {healthData?.work_balance || 'Good'}
              </Badge>
            </div>
            <div className="pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-600">Engagement Score</span>
                <span className="font-semibold text-slate-900">{healthData?.engagement_score || 82}%</span>
              </div>
              <Progress value={healthData?.engagement_score || 82} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card data-testid="quick-stats-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quick Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Leave Balance</p>
                <p className="font-semibold text-slate-900">
                  {typeof dashboardData?.leave_balance === 'object' 
                    ? `${dashboardData.leave_balance.annual || 0} days` 
                    : `${dashboardData?.leave_balance || 21} days`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Training Completed</p>
                <p className="font-semibold text-slate-900">{dashboardData?.training_completed || 0} courses</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Training In Progress</p>
                <p className="font-semibold text-slate-900">{dashboardData?.training_in_progress || 0} courses</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Payroll */}
        <Card data-testid="recent-payroll-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <DollarSign className="w-5 h-5 text-emerald-500" />
              Recent Payroll
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_payroll?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recent_payroll.map((pay, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{pay.month} {pay.year}</p>
                      <p className="text-sm text-slate-500">{pay.status}</p>
                    </div>
                    <p className="font-semibold text-emerald-600">
                      R {pay.net_salary?.toLocaleString() || '0'}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No payroll records yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
